import React from "react";
import { useAuth } from "../../contexts/auth-context";
import { useTranslation } from "react-i18next";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Chip,
  Avatar,
  Grid,
} from "@mui/material";
import {
  User,
  Mail,
  Shield,
  CreditCard,
} from "lucide-react";
import BillingPortalButton from "../../components/subscription/BillingPortalButton";

const ProfilePage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case "ADMIN":
        return "error";
      case "MODERATOR":
        return "warning";
      case "MODERATOR_FREE":
        return "info";
      case "SAFEE_MODERATOR":
        return "secondary";
      case "GUEST":
        return "default";
      default:
        return "default";
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case "ADMIN":
        return t('profile.roles.admin');
      case "MODERATOR":
        return t('profile.roles.moderator');
      case "MODERATOR_FREE":
        return t('profile.roles.moderatorFree');
      case "SAFEE_MODERATOR":
        return t('profile.roles.safeeModerator');
      case "GUEST":
        return t('profile.roles.guest');
      case "USER":
        return t('profile.roles.user');
      default:
        return role;
    }
  };

  if (!user) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>{t('profile.loading')}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 4, fontWeight: 600 }}>
        {t('profile.title')}
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card elevation={2}>
            <CardContent sx={{ textAlign: "center", py: 4 }}>
              <Avatar
                sx={{
                  width: 120,
                  height: 120,
                  mx: "auto",
                  mb: 2,
                  bgcolor: "primary.main",
                  fontSize: "3rem",
                }}
              >
                {user.fullname ? user.fullname.charAt(0).toUpperCase() : user.email.charAt(0).toUpperCase()}
              </Avatar>
              <Typography variant="h5" gutterBottom>
                {user.fullname || t('profile.user')}
              </Typography>
              <Chip
                label={getRoleLabel(user.role)}
                color={getRoleBadgeColor(user.role)}
                size="small"
                sx={{ mt: 1 }}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
                {t('profile.personalInfo')}
              </Typography>

              <Stack spacing={3}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <User size={20} style={{ color: "#666" }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t('profile.fullName')}
                    </Typography>
                    <Typography variant="body1">
                      {user.fullname || t('profile.notProvided')}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Mail size={20} style={{ color: "#666" }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t('profile.email')}
                    </Typography>
                    <Typography variant="body1">{user.email}</Typography>
                  </Box>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Shield size={20} style={{ color: "#666" }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t('profile.role')}
                    </Typography>
                    <Typography variant="body1">
                      {getRoleLabel(user.role)}
                    </Typography>
                  </Box>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          {/* Ne pas afficher la section Subscription pour MODERATOR sans Stripe (contrat direct) */}
          {!(user.role === 'MODERATOR' && !user.stripeCustomerId) && (
            <Card elevation={2} sx={{ mt: 3 }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                  <CreditCard size={24} style={{ color: "#666" }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t('profile.subscription.title')}
                  </Typography>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {user.role === 'ADMIN' || user.role === 'MODERATOR_FREE' || user.stripeCustomerId ? (
                    t('profile.subscription.adminDesc')
                  ) : (
                    t('profile.subscription.companyManaged')
                  )}
                </Typography>

                {user.role === 'ADMIN' || user.role === 'MODERATOR_FREE' || user.stripeCustomerId ? (
                  user.stripeCustomerId ? (
                    <BillingPortalButton
                      customerId={user.stripeCustomerId}
                      onError={(error) => console.error("Erreur portail:", error)}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {t('profile.subscription.noActive')}
                    </Typography>
                  )
                ) : (
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {t('profile.subscription.managedBy', { role: getRoleLabel(user.role) })}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProfilePage;
